<template>
  <div v-if="responsiveLayout" class="responsive-wrapper">
    <button
      type="button"
      :disabled="disabled || loading"
      :aria-label="ariaLabel"
      :aria-expanded="ariaExpanded"
      :aria-controls="ariaControls"
      :class="['app-button-' + variant, computedButtonClasses]"
      @click="handleClick"
    >
      <template v-if="loading">
        <svg class="tw-animate-spin tw-h-4 tw-w-4 tw-mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle class="tw-opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
          <path class="tw-opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
        <span>{{ loadingText || displayText }}</span>
      </template>
      <template v-else>
        <i v-if="displayIcon" :class="displayIcon" aria-hidden="true" style="display: inline-flex; align-items: center;"></i>
        <span :style="displayIcon ? 'margin-left: 0.5rem; display: inline-flex; align-items: center;' : ''">{{ displayText }}</span>
      </template>
    </button>
  </div>
  <button
    v-else
    type="button"
    :disabled="disabled || loading"
    :aria-label="ariaLabel"
    :aria-expanded="ariaExpanded"
    :aria-controls="ariaControls"
    :class="computedButtonClasses"
    :style="buttonInlineStyles"
    @click="handleClick"
  >
    <template v-if="loading">
      <svg class="tw-animate-spin tw-h-4 tw-w-4 tw-mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
        <circle class="tw-opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
        <path class="tw-opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
      </svg>
      <span>{{ loadingText || displayText }}</span>
    </template>
    <template v-else>
      <i v-if="displayIcon" :class="displayIcon" aria-hidden="true"></i>
      <span :class="[displayIcon ? 'tw-ml-2' : '', (disabled || loading) ? 'disabled-text' : '']">{{ displayText }}</span>
    </template>
  </button>
</template>

<script>
import {loadPage} from '@/helpers/routing'

export default {
  name: 'AppButton',
  props: {
    // Contenu
    text: {
      type: String,
      default: null
    },
    icon: {
      type: String,
      default: null
    },
    
    // États
    loading: {
      type: Boolean,
      default: false
    },
    loadingText: {
      type: String,
      default: null
    },
    disabled: {
      type: Boolean,
      default: false
    },
    
    // Apparence
    variant: {
      type: String,
      default: 'primary',
      validator: value => ['primary', 'secondary', 'success', 'danger', 'warning', 'info', 'light', 'dark', 'link'].includes(value)
    },
    size: {
      type: String,
      default: 'md',
      validator: value => ['sm', 'md', 'lg'].includes(value)
    },
    block: {
      type: Boolean,
      default: false
    },
    outline: {
      type: Boolean,
      default: false
    },
    
    // Accessibilité
    ariaLabel: {
      type: String,
      default: null
    },
    ariaExpanded: {
      type: [Boolean, String],
      default: null
    },
    ariaControls: {
      type: String,
      default: null
    },
    
    // Classes CSS supplémentaires
    customClass: {
      type: String,
      default: ''
    },
    
    // Layout responsive automatique
    responsiveLayout: {
      type: Boolean,
      default: false
    },
    
    // Props spécifiques au bouton retour (backButton)
    isBackButton: {
      type: Boolean,
      default: false
    },
    returnUrl: {
      type: String,
      default: 'none'
    },
    backLabel: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    computedButtonClasses() {
      const classes = ['tw-inline-flex tw-items-center tw-justify-center tw-font-medium tw-rounded tw-transition-colors tw-duration-200 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-offset-2']
      
      // Taille
      if (this.size === 'lg') {
        classes.push('tw-px-6 tw-py-3 tw-text-base')
      } else if (this.size === 'sm') {
        classes.push('tw-px-3 tw-py-1.5 tw-text-sm')
      } else {
        classes.push('tw-px-4 tw-py-2 tw-text-sm')
      }
      
      // Variantes de couleur
      if (this.variant === 'primary') {
        if (this.outline) {
          classes.push('tw-text-teal-700 tw-border tw-border-teal-700 tw-bg-transparent hover:tw-bg-teal-700 hover:tw-text-white focus:tw-ring-teal-500')
        } else {
          classes.push('tw-bg-teal-700 tw-text-white hover:tw-bg-teal-800 focus:tw-ring-teal-500')
        }
      } else if (this.variant === 'secondary') {
        if (this.outline) {
          classes.push('tw-text-gray-700 tw-border tw-border-gray-300 tw-bg-transparent hover:tw-bg-gray-50 focus:tw-ring-gray-500')
        } else {
          classes.push('tw-bg-gray-600 tw-text-white hover:tw-bg-gray-700 focus:tw-ring-gray-500')
        }
      } else if (this.variant === 'light') {
        // Utilisation de style inline pour s'assurer que ça fonctionne
        classes.push('tw-bg-gray-100 tw-text-gray-700 tw-border tw-border-gray-300 hover:tw-bg-gray-200 focus:tw-ring-gray-500 tw-min-h-[38px]')
      } else if (this.variant === 'danger') {
        if (this.outline) {
          classes.push('tw-text-red-700 tw-border tw-border-red-700 tw-bg-transparent hover:tw-bg-red-700 hover:tw-text-white focus:tw-ring-red-500')
        } else {
          classes.push('tw-bg-red-600 tw-text-white hover:tw-bg-red-700 focus:tw-ring-red-500')
        }
      } else if (this.variant === 'success') {
        if (this.outline) {
          classes.push('tw-text-green-700 tw-border tw-border-green-700 tw-bg-transparent hover:tw-bg-green-700 hover:tw-text-white focus:tw-ring-green-500')
        } else {
          classes.push('tw-bg-green-600 tw-text-white hover:tw-bg-green-700 focus:tw-ring-green-500')
        }
      } else if (this.variant === 'link') {
        classes.push('tw-text-teal-600 hover:tw-text-teal-700 hover:tw-underline tw-bg-transparent tw-border-0 tw-p-0')
      }
      
      // État désactivé
      if (this.disabled || this.loading) {
        classes.push('disabled:tw-cursor-not-allowed')
        if (this.variant === 'light') {
          classes.push('disabled:tw-bg-gray-200 disabled:tw-text-black disabled:tw-border-gray-300')
        } else {
          classes.push('disabled:tw-opacity-50')
        }
      }
      
      // Largeur complète
      if (this.block || this.responsiveLayout) {
        classes.push('tw-w-full')
      }
      
      // Classes personnalisées
      if (this.customClass) {
        classes.push(this.customClass)
      }
      
      return classes.join(' ')
    },
    wrapperClasses() {
      // Classes pour le wrapper div qui gère le responsive
      return 'tw-w-full md:tw-w-auto md:tw-flex-1'
    },
    wrapperInlineStyles() {
      // Styles inline pour le wrapper responsive
      return {
        width: '100%',
        '@media (min-width: 768px)': {
          width: 'auto',
          flex: '1'
        }
      }
    },
    buttonClasses() {
      // Gardé pour la compatibilité
      return ''
    },
    buttonInlineStyles() {
      // Styles inline de secours pour garantir le fonctionnement
      const styles = {
        width: this.block || this.responsiveLayout ? '100%' : 'auto',
        padding: '0.5rem 1rem',
        borderRadius: '0.375rem',
        fontWeight: '500',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '0',
        transition: 'all 0.2s',
        cursor: this.disabled || this.loading ? 'not-allowed' : 'pointer'
      }
      
      // Styles selon la variante
      if (this.variant === 'light') {
        if (this.disabled || this.loading) {
          styles.backgroundColor = '#e5e7eb'
          styles.color = '#000000'
          styles.border = '1px solid #d1d5db'
        } else {
          styles.backgroundColor = '#f3f4f6'
          styles.color = '#374151'
          styles.border = '1px solid #d1d5db'
        }
      }
      
      return styles
    },
    displayText() {
      if (this.isBackButton) {
        return this.$t('common.bck')
      }
      return this.text
    },
    displayIcon() {
      if (this.isBackButton) {
        return 'fa-solid fa-arrow-left'
      }
      return this.icon
    }
  },
  methods: {
    handleClick(event) {
      if (this.isBackButton) {
        this.goBack(this.returnUrl)
      } else {
        this.$emit('click', event)
      }
    },
    goBack(url) {
      if (url !== 'none') {
        loadPage(this, {url: url})
      } else {
        /* On évite d'afficher la page de login a un utilisateur deja connecte */
        /* Je bidouille pour tester */
        if (this.$f7.mainView.history[this.$f7.mainView.history.length - 2] === '/login/') {
            loadPage(this, {url: '/home/'})
        } else {
          if (this.$f7.mainView.history[this.$f7.mainView.history.length - 2] === this.$f7.mainView.history[this.$f7.mainView.history.length - 1]) {
            loadPage(this, {url: '/home/'})
          } else {
            this.$f7.mainView.router.back()
          }
          this.$bus.$emit('router-back')
        }
      }
    }
  }
}
</script>

<style scoped>
/* Style de secours pour le responsive */
.responsive-wrapper {
  width: 100%;
}

@media (min-width: 768px) {
  .responsive-wrapper {
    width: auto;
    flex: 1;
  }
}

/* Style de base pour les boutons */
.app-button-light {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.5rem 1rem;
  font-weight: 500;
  border-radius: 0.375rem;
  transition: all 0.2s;
  width: 100%;
  background-color: #f3f4f6;
  color: #374151;
  border: 1px solid #d1d5db;
}

.app-button-light:hover:not(:disabled) {
  background-color: #e5e7eb;
}

.app-button-light:focus {
  outline: 2px solid #0C6E6D;
  outline-offset: 2px;
}

.app-button-light:disabled {
  background-color: #e5e7eb !important;
  color: #000000 !important;
  border-color: #d1d5db !important;
  cursor: not-allowed;
}

.app-button-light:disabled span {
  color: #000000 !important;
}

button:disabled span {
  color: #000000 !important;
}

.disabled-text {
  color: #000000 !important;
}

/* Force le contraste pour tous les boutons désactivés */
button:disabled {
  background-color: #e5e7eb !important;
  color: #000000 !important;
}

button:disabled span {
  color: #000000 !important;
}

.disabled-text {
  color: #000000 !important;
}
</style>